import React, { useState, useEffect, useCallback, useRef } from 'react';
import { sponsorStoryService } from '../services/sponsorStoryService';
import { SponsorStory, CreateSponsorStoryData, SponsorStoryStats, SPONSOR_SPORT_OPTIONS } from '../types/models/sponsorStory';
import { useAuth } from '../contexts/AuthContext';

const SponsorStoryManagement: React.FC = () => {
  // Activity page Sponsor Stories image is roughly ~1.28:1 on common devices (full-width card with fixed height).
  // 9:7 closely matches this and avoids aggressive top/bottom cropping seen with wider ratios like 2:1.
  const COVER_ASPECT_RATIO = 9 / 7;
  const COVER_EXPORT_WIDTH = 1260;
  const COVER_EXPORT_HEIGHT = Math.round(COVER_EXPORT_WIDTH / COVER_ASPECT_RATIO);

  const { adminProfile } = useAuth();
  const [stories, setStories] = useState<SponsorStory[]>([]);
  const [stats, setStats] = useState<SponsorStoryStats>({ total: 0, active: 0, featured: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingStory, setEditingStory] = useState<SponsorStory | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');

  // User search for athlete field
  const [userSearchResults, setUserSearchResults] = useState<Array<{ id: string; uid: string; displayName: string; photoURL: string | null }>>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const athleteFieldRef = useRef<HTMLDivElement>(null);

  // Pending local File objects chosen by the user — uploaded on submit
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [athleteFile, setAthleteFile] = useState<File | null>(null);
  // Local object URLs for instant preview before upload
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [athletePreview, setAthletePreview] = useState<string | null>(null);

  const emptyForm: CreateSponsorStoryData = {
    athleteName: '',
    athleteUserId: undefined,
    sport: 'cricket',
    sponsorBrand: '',
    dealDescription: '',
    coverImageUrl: '',
    athleteImageUrl: '',
    dealAmount: '',
    location: '',
    isFeatured: false,
    publishedAt: '',
  };
  const [form, setForm] = useState<CreateSponsorStoryData>(emptyForm);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [allStories, storyStats] = await Promise.all([
        sponsorStoryService.getAllStories(),
        sponsorStoryService.getStats(),
      ]);
      setStories(allStories);
      setStats(storyStats);
    } catch (err) {
      setError('Failed to load sponsor stories. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const resetFileState = () => {
    setCoverFile(null);
    setAthleteFile(null);
    if (coverPreview) URL.revokeObjectURL(coverPreview);
    if (athletePreview) URL.revokeObjectURL(athletePreview);
    setCoverPreview(null);
    setAthletePreview(null);
  };

  const resetUserSearch = () => {
    setUserSearchResults([]);
    setShowUserDropdown(false);
    setUserSearchLoading(false);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
  };

  const handleAthleteNameChange = (value: string) => {
    setForm(f => ({ ...f, athleteName: value, athleteUserId: undefined }));
    setShowUserDropdown(true);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!value.trim()) { setUserSearchResults([]); return; }
    searchTimeoutRef.current = setTimeout(async () => {
      setUserSearchLoading(true);
      try {
        const results = await sponsorStoryService.searchUsers(value);
        setUserSearchResults(results);
      } catch { setUserSearchResults([]); }
      finally { setUserSearchLoading(false); }
    }, 300);
  };

  const selectUser = (user: { id: string; uid: string; displayName: string; photoURL: string | null }) => {
    setForm(f => ({ ...f, athleteName: user.displayName, athleteUserId: user.uid }));
    setUserSearchResults([]);
    setShowUserDropdown(false);
  };

  const openCreateModal = () => {
    setEditingStory(null);
    setForm(emptyForm);
    resetFileState();
    resetUserSearch();
    setShowModal(true);
  };

  const openEditModal = (story: SponsorStory) => {
    setEditingStory(story);
    resetUserSearch();
    setForm({
      athleteName: story.athleteName,
      athleteUserId: story.athleteUserId,
      sport: story.sport,
      sponsorBrand: story.sponsorBrand,
      dealDescription: story.dealDescription,
      coverImageUrl: story.coverImageUrl || '',
      athleteImageUrl: story.athleteImageUrl || '',
      dealAmount: story.dealAmount || '',
      location: story.location || '',
      isFeatured: story.isFeatured,
      publishedAt: story.publishedAt ? story.publishedAt.slice(0, 16) : '',
    });
    resetFileState();
    setShowModal(true);
  };

  const cropImageToAspectRatio = async (
    file: File,
    aspectRatio: number,
    outputWidth: number,
    outputHeight: number
  ): Promise<File> => {
    const imageUrl = URL.createObjectURL(file);
    try {
      const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to load selected image.'));
        img.src = imageUrl;
      });

      const sourceAspect = image.width / image.height;
      let sx = 0;
      let sy = 0;
      let sWidth = image.width;
      let sHeight = image.height;

      if (sourceAspect > aspectRatio) {
        sWidth = Math.floor(image.height * aspectRatio);
        sx = Math.floor((image.width - sWidth) / 2);
      } else if (sourceAspect < aspectRatio) {
        sHeight = Math.floor(image.width / aspectRatio);
        sy = Math.floor((image.height - sHeight) / 2);
      }

      const canvas = document.createElement('canvas');
      canvas.width = outputWidth;
      canvas.height = outputHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not initialize image processing.');

      ctx.drawImage(image, sx, sy, sWidth, sHeight, 0, 0, outputWidth, outputHeight);

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((generatedBlob) => {
          if (!generatedBlob) {
            reject(new Error('Failed to process image.'));
            return;
          }
          resolve(generatedBlob);
        }, 'image/jpeg', 0.92);
      });

      const sanitizedName = file.name.replace(/\.[^/.]+$/, '');
      return new File([blob], `${sanitizedName}_cover.jpg`, { type: 'image/jpeg' });
    } finally {
      URL.revokeObjectURL(imageUrl);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, type: 'cover' | 'athlete') => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      if (type === 'cover') {
        const processedCoverFile = await cropImageToAspectRatio(
          file,
          COVER_ASPECT_RATIO,
          COVER_EXPORT_WIDTH,
          COVER_EXPORT_HEIGHT
        );
        const preview = URL.createObjectURL(processedCoverFile);
        if (coverPreview) URL.revokeObjectURL(coverPreview);
        setCoverFile(processedCoverFile);
        setCoverPreview(preview);
        setForm(f => ({ ...f, coverImageUrl: preview })); // placeholder; real URL set after upload
      } else {
        const preview = URL.createObjectURL(file);
        if (athletePreview) URL.revokeObjectURL(athletePreview);
        setAthleteFile(file);
        setAthletePreview(preview);
        setForm(f => ({ ...f, athleteImageUrl: preview }));
      }
    } catch (error) {
      console.error(error);
      alert('Failed to process selected image. Please try another image.');
    } finally {
      e.target.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.athleteName.trim() || !form.sponsorBrand.trim() || !form.dealDescription.trim()) {
      alert('Athlete name, sponsor brand, and deal description are required.');
      return;
    }
    setSubmitting(true);
    try {
      // Upload any new files first — replace the blob preview URL with the real CDN URL
      const updatedForm = { ...form };
      if (coverFile) {
        updatedForm.coverImageUrl = await sponsorStoryService.uploadImage(coverFile, 'cover');
      }
      if (athleteFile) {
        updatedForm.athleteImageUrl = await sponsorStoryService.uploadImage(athleteFile, 'athlete');
      }

      const adminName = adminProfile?.role || 'Admin';
      if (editingStory) {
        await sponsorStoryService.updateStory(editingStory.id, updatedForm);
      } else {
        await sponsorStoryService.createStory(updatedForm, adminName);
      }
      setShowModal(false);
      resetFileState();
      await loadData();
    } catch (err) {
      alert('Failed to save sponsor story. Please try again.');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (story: SponsorStory) => {
    if (!window.confirm(`Delete "${story.athleteName}'s" story? This cannot be undone.`)) return;
    try {
      await sponsorStoryService.deleteStory(story.id);
      await loadData();
    } catch {
      alert('Failed to delete story.');
    }
  };

  const handleToggleActive = async (story: SponsorStory) => {
    try {
      await sponsorStoryService.toggleActive(story.id, !story.isActive);
      await loadData();
    } catch {
      alert('Failed to update story status.');
    }
  };

  const handleToggleFeatured = async (story: SponsorStory) => {
    try {
      await sponsorStoryService.toggleFeatured(story.id, !story.isFeatured);
      await loadData();
    } catch {
      alert('Failed to update featured status.');
    }
  };

  const filteredStories = stories.filter((s) => {
    if (filterActive === 'active') return s.isActive;
    if (filterActive === 'inactive') return !s.isActive;
    return true;
  });

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <style>{`
        .ss-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 8px; font-size: 14px; font-weight: 500; cursor: pointer; border: none; transition: background 0.15s; }
        .ss-btn-primary { background: #2563eb; color: #fff; }
        .ss-btn-primary:hover { background: #1d4ed8; }
        .ss-btn-success { background: #16a34a; color: #fff; }
        .ss-btn-success:hover { background: #15803d; }
        .ss-btn-danger { background: #dc2626; color: #fff; }
        .ss-btn-danger:hover { background: #b91c1c; }
        .ss-btn-ghost { background: transparent; color: #374151; border: 1px solid #d1d5db; }
        .ss-btn-ghost:hover { background: #f3f4f6; }
        .ss-badge { display: inline-block; padding: 2px 8px; border-radius: 99px; font-size: 12px; font-weight: 500; }
        .ss-badge-green { background: #dcfce7; color: #15803d; }
        .ss-badge-red { background: #fee2e2; color: #b91c1c; }
        .ss-badge-yellow { background: #fef9c3; color: #854d0e; }
        .ss-badge-blue { background: #dbeafe; color: #1e40af; }
        .ss-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden; }
        .ss-table { width: 100%; border-collapse: collapse; }
        .ss-table th { background: #f9fafb; padding: 12px 16px; text-align: left; font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #e5e7eb; }
        .ss-table td { padding: 14px 16px; border-bottom: 1px solid #f3f4f6; font-size: 14px; color: #374151; vertical-align: middle; }
        .ss-table tr:last-child td { border-bottom: none; }
        .ss-table tr:hover td { background: #f9fafb; }
        .ss-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 16px; }
        .ss-modal { background: #fff; border-radius: 16px; width: 100%; max-width: 640px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(0,0,0,0.3); }
        .ss-modal-header { padding: 20px 24px; border-bottom: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center; }
        .ss-modal-body { padding: 24px; display: flex; flex-direction: column; gap: 16px; }
        .ss-modal-footer { padding: 16px 24px; border-top: 1px solid #e5e7eb; display: flex; justify-content: flex-end; gap: 12px; }
        .ss-field label { display: block; font-size: 13px; font-weight: 500; color: #374151; margin-bottom: 6px; }
        .ss-field input, .ss-field textarea, .ss-field select { width: 100%; padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; outline: none; box-sizing: border-box; }
        .ss-field input:focus, .ss-field textarea:focus, .ss-field select:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
        .ss-field textarea { resize: vertical; min-height: 80px; }
        .ss-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .ss-stat { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 20px; }
        .ss-athlete-img { width: 36px; height: 36px; border-radius: 50%; object-fit: cover; }
        .ss-athlete-placeholder { width: 36px; height: 36px; border-radius: 50%; background: #dbeafe; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 600; color: #1e40af; flex-shrink: 0; }
        .ss-user-dropdown { position: absolute; top: 100%; left: 0; right: 0; background: #fff; border: 1px solid #d1d5db; border-radius: 8px; box-shadow: 0 8px 24px rgba(0,0,0,0.12); z-index: 100; max-height: 220px; overflow-y: auto; margin-top: 4px; }
        .ss-user-option { display: flex; align-items: center; gap: 10px; padding: 8px 12px; cursor: pointer; transition: background 0.1s; font-size: 14px; color: #111827; }
        .ss-user-option:hover { background: #f3f4f6; }
        .ss-user-avatar { width: 28px; height: 28px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
        .ss-user-avatar-placeholder { width: 28px; height: 28px; border-radius: 50%; background: #dbeafe; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 600; color: #1e40af; flex-shrink: 0; }
        .ss-linked-badge { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 99px; font-size: 11px; font-weight: 500; background: #dcfce7; color: #15803d; margin-top: 4px; }
        @media (max-width: 640px) { .ss-grid2 { grid-template-columns: 1fr; } }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', margin: 0 }}>Sponsor Stories</h1>
          <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
            Manage athlete sponsorship stories shown on the app home screen
          </p>
        </div>
        <button className="ss-btn ss-btn-primary" onClick={openCreateModal}>
          + Add Story
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <div className="ss-stat">
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#1e40af' }}>{stats.total}</div>
          <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>Total Stories</div>
        </div>
        <div className="ss-stat">
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#15803d' }}>{stats.active}</div>
          <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>Active (shown in app)</div>
        </div>
        <div className="ss-stat">
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#854d0e' }}>{stats.featured}</div>
          <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>Featured</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {(['all', 'active', 'inactive'] as const).map((f) => (
          <button
            key={f}
            className={`ss-btn ${filterActive === f ? 'ss-btn-primary' : 'ss-btn-ghost'}`}
            style={{ padding: '6px 14px', fontSize: '13px' }}
            onClick={() => setFilterActive(f)}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', color: '#b91c1c', fontSize: '14px' }}>
          {error}
        </div>
      )}

      {/* Table */}
      <div className="ss-card">
        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>Loading stories...</div>
        ) : filteredStories.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>
            No stories found. Click "Add Story" to create the first one.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="ss-table">
              <thead>
                <tr>
                  <th>Athlete</th>
                  <th>Sport</th>
                  <th>Sponsor</th>
                  <th>Deal</th>
                  <th>Status</th>
                  <th>Featured</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStories.map((story) => (
                  <tr key={story.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {story.athleteImageUrl ? (
                          <img src={story.athleteImageUrl} alt={story.athleteName} className="ss-athlete-img" />
                        ) : (
                          <div className="ss-athlete-placeholder">
                            {story.athleteName.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div style={{ fontWeight: '500', color: '#111827' }}>{story.athleteName}</div>
                          {story.location && <div style={{ fontSize: '12px', color: '#9ca3af' }}>{story.location}</div>}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="ss-badge ss-badge-blue">
                        {SPONSOR_SPORT_OPTIONS.find(o => o.value === story.sport)?.label || story.sport}
                      </span>
                    </td>
                    <td style={{ fontWeight: '500' }}>{story.sponsorBrand}</td>
                    <td style={{ maxWidth: '220px' }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#374151' }}>
                        {story.dealDescription}
                      </div>
                      {story.dealAmount && (
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{story.dealAmount}</div>
                      )}
                    </td>
                    <td>
                      <button
                        className={`ss-badge ${story.isActive ? 'ss-badge-green' : 'ss-badge-red'}`}
                        style={{ cursor: 'pointer', border: 'none' }}
                        onClick={() => handleToggleActive(story)}
                        title="Click to toggle"
                      >
                        {story.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td>
                      <button
                        className={`ss-badge ${story.isFeatured ? 'ss-badge-yellow' : 'ss-badge-blue'}`}
                        style={{ cursor: 'pointer', border: 'none' }}
                        onClick={() => handleToggleFeatured(story)}
                        title="Click to toggle"
                      >
                        {story.isFeatured ? 'Featured' : 'Normal'}
                      </button>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          className="ss-btn ss-btn-ghost"
                          style={{ padding: '5px 12px', fontSize: '13px' }}
                          onClick={() => openEditModal(story)}
                        >
                          Edit
                        </button>
                        <button
                          className="ss-btn ss-btn-danger"
                          style={{ padding: '5px 12px', fontSize: '13px' }}
                          onClick={() => handleDelete(story)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="ss-modal-bg" onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className="ss-modal">
            <div className="ss-modal-header">
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                {editingStory ? 'Edit Sponsor Story' : 'Add Sponsor Story'}
              </h2>
              <button
                className="ss-btn ss-btn-ghost"
                style={{ padding: '4px 10px' }}
                onClick={() => { setShowModal(false); resetFileState(); }}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="ss-modal-body">
                <div className="ss-grid2">
                  <div className="ss-field" style={{ position: 'relative' }} ref={athleteFieldRef}>
                    <label>Athlete Name *</label>
                    <input
                      type="text"
                      placeholder="Search by name..."
                      value={form.athleteName}
                      onChange={(e) => handleAthleteNameChange(e.target.value)}
                      onFocus={() => form.athleteName && setShowUserDropdown(true)}
                      onBlur={() => setTimeout(() => setShowUserDropdown(false), 150)}
                      autoComplete="off"
                      required
                    />
                    {form.athleteUserId && (
                      <div className="ss-linked-badge">✓ Linked to profile</div>
                    )}
                    {showUserDropdown && (
                      <div className="ss-user-dropdown">
                        {userSearchLoading ? (
                          <div style={{ padding: '10px 12px', color: '#6b7280', fontSize: '13px' }}>Searching...</div>
                        ) : userSearchResults.length === 0 ? (
                          <div style={{ padding: '10px 12px', color: '#6b7280', fontSize: '13px' }}>No users found</div>
                        ) : userSearchResults.map((u) => (
                          <div key={u.uid} className="ss-user-option" onMouseDown={() => selectUser(u)}>
                            {u.photoURL ? (
                              <img src={u.photoURL} alt={u.displayName} className="ss-user-avatar" />
                            ) : (
                              <div className="ss-user-avatar-placeholder">{u.displayName.charAt(0).toUpperCase()}</div>
                            )}
                            <span>{u.displayName}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="ss-field">
                    <label>Sport *</label>
                    <select
                      value={form.sport}
                      onChange={(e) => setForm({ ...form, sport: e.target.value as any })}
                    >
                      {SPONSOR_SPORT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="ss-grid2">
                  <div className="ss-field">
                    <label>Sponsor Brand *</label>
                    <input
                      type="text"
                      placeholder="e.g. Nike, Adidas"
                      value={form.sponsorBrand}
                      onChange={(e) => setForm({ ...form, sponsorBrand: e.target.value })}
                      required
                    />
                  </div>
                  <div className="ss-field">
                    <label>Deal Amount (optional)</label>
                    <input
                      type="text"
                      placeholder="e.g. ₹5 Lakh/year"
                      value={form.dealAmount}
                      onChange={(e) => setForm({ ...form, dealAmount: e.target.value })}
                    />
                  </div>
                </div>

                <div className="ss-field">
                  <label>Deal Description *</label>
                  <textarea
                    placeholder="Describe the sponsorship deal, how AmaPlayer helped, athlete's achievements..."
                    value={form.dealDescription}
                    onChange={(e) => setForm({ ...form, dealDescription: e.target.value })}
                    required
                  />
                </div>

                <div className="ss-grid2">
                  {/* Cover Image Upload */}
                  <div className="ss-field">
                    <label>Cover Image</label>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>
                      Auto-cropped to the Activity card ratio (9:7) from center.
                    </div>
                    <label style={{ cursor: 'pointer', display: 'block' }}>
                      <div style={{
                        border: '2px dashed #d1d5db', borderRadius: '10px', overflow: 'hidden',
                        height: '110px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: '#f9fafb', position: 'relative',
                        transition: 'border-color 0.15s',
                      }}>
                        {coverPreview || form.coverImageUrl ? (
                          <img
                            src={coverPreview || form.coverImageUrl}
                            alt="Cover"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }}
                          />
                        ) : null}
                        <div style={{
                          position: 'relative', zIndex: 1, textAlign: 'center', padding: '8px',
                          background: (coverPreview || form.coverImageUrl) ? 'rgba(0,0,0,0.45)' : 'transparent',
                          borderRadius: 8,
                        }}>
                          <div style={{ fontSize: '22px' }}>🖼️</div>
                          <div style={{ fontSize: '12px', color: (coverPreview || form.coverImageUrl) ? '#fff' : '#6b7280', marginTop: '2px' }}>
                            {coverPreview || form.coverImageUrl ? 'Click to change' : 'Click to upload'}
                          </div>
                        </div>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => handleFileChange(e, 'cover')}
                      />
                    </label>
                  </div>

                  {/* Athlete Photo Upload */}
                  <div className="ss-field">
                    <label>Athlete Photo</label>
                    <label style={{ cursor: 'pointer', display: 'block' }}>
                      <div style={{
                        border: '2px dashed #d1d5db', borderRadius: '10px', overflow: 'hidden',
                        height: '110px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: '#f9fafb', position: 'relative',
                        transition: 'border-color 0.15s',
                      }}>
                        {athletePreview || form.athleteImageUrl ? (
                          <img
                            src={athletePreview || form.athleteImageUrl}
                            alt="Athlete"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }}
                          />
                        ) : null}
                        <div style={{
                          position: 'relative', zIndex: 1, textAlign: 'center', padding: '8px',
                          background: (athletePreview || form.athleteImageUrl) ? 'rgba(0,0,0,0.45)' : 'transparent',
                          borderRadius: 8,
                        }}>
                          <div style={{ fontSize: '22px' }}>👤</div>
                          <div style={{ fontSize: '12px', color: (athletePreview || form.athleteImageUrl) ? '#fff' : '#6b7280', marginTop: '2px' }}>
                            {athletePreview || form.athleteImageUrl ? 'Click to change' : 'Click to upload'}
                          </div>
                        </div>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => handleFileChange(e, 'athlete')}
                      />
                    </label>
                  </div>
                </div>

                <div className="ss-grid2">
                  <div className="ss-field">
                    <label>Location</label>
                    <input
                      type="text"
                      placeholder="e.g. Mumbai, Maharashtra"
                      value={form.location}
                      onChange={(e) => setForm({ ...form, location: e.target.value })}
                    />
                  </div>
                  <div className="ss-field">
                    <label>Publish Date</label>
                    <input
                      type="datetime-local"
                      value={form.publishedAt}
                      onChange={(e) => setForm({ ...form, publishedAt: e.target.value })}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    id="isFeatured"
                    checked={form.isFeatured}
                    onChange={(e) => setForm({ ...form, isFeatured: e.target.checked })}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  <label htmlFor="isFeatured" style={{ fontSize: '14px', color: '#374151', cursor: 'pointer', margin: 0 }}>
                    Featured story (shown first in carousel)
                  </label>
                </div>
              </div>
              <div className="ss-modal-footer">
                <button type="button" className="ss-btn ss-btn-ghost" onClick={() => { setShowModal(false); resetFileState(); }}>
                  Cancel
                </button>
                <button type="submit" className="ss-btn ss-btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : editingStory ? 'Save Changes' : 'Create Story'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SponsorStoryManagement;
